import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, ChevronDown, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { CONTACT, SERVICE_OPTIONS, SOCIALS, whatsappLink } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  service: "",
  message: "",
  website: "",
};

const INFO = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: CONTACT.whatsappDisplay,
    href: whatsappLink("Hello Faithh! I'd like a free consultation."),
    testId: "contact-info-whatsapp",
  },
  {
    icon: Phone,
    label: "Phone",
    value: CONTACT.phoneDisplay,
    href: `tel:${CONTACT.phone}`,
    testId: "contact-info-phone",
  },
  {
    icon: Mail,
    label: "Email",
    value: CONTACT.email,
    href: `mailto:${CONTACT.email}`,
    testId: "contact-info-email",
  },
  {
    icon: MapPin,
    label: "Location",
    value: CONTACT.location,
    href: null,
    testId: "contact-info-location",
  },
];

const inputClass =
  "w-full rounded-xl border border-white/10 bg-navy-950/70 px-5 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 transition-[border-color,box-shadow] duration-300 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/25";

export const Contact = ({ preselectedService }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (preselectedService) {
      setForm((f) => ({ ...f, service: preselectedService }));
    }
  }, [preselectedService]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.website) {
      setDone(true);
      return;
    }
    if (!form.service) {
      toast.error("Please select the service you need.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success(res.data?.message || "Your enquiry has been received.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? "Please check your details and try again."
        : detail || "Something went wrong — please reach us on WhatsApp instead.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const waMessage = `Hello Faithh! I'm ${form.full_name || "interested in your services"}${
    form.service ? ` — I'm looking for help with ${form.service}` : ""
  }. ${form.message || ""}`.trim();

  return (
    <section id="contact" data-testid="contact-section" className="noise-overlay relative bg-navy-900 py-24 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.07),transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeading
          overline="Contact Us"
          testId="contact-heading"
          title={
            <>
              Let's talk about <span className="text-gold-metallic italic">your business</span>
            </>
          }
          description="Tell us what you need — compliance, a website, an app, marketing or all of it. We reply personally, usually the same day."
        />

        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-14">
          <div className="space-y-4 lg:col-span-2">
            {INFO.map((item, i) => {
              const Icon = item.icon;
              const inner = (
                <>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-gold/25 bg-gold/8 transition-all duration-500 group-hover:bg-gold/15">
                    <Icon className="h-5 w-5 text-gold" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.26em] text-slate-500">{item.label}</span>
                    <span className="mt-1 block text-sm sm:text-base font-semibold text-slate-100">{item.value}</span>
                  </span>
                </>
              );
              return (
                <Reveal key={item.label} delay={0.07 * i}>
                  {item.href ? (
                    <a
                      href={item.href}
                      data-testid={item.testId}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="card-lift group flex items-center gap-5 rounded-2xl border border-white/8 bg-navy-850/70 p-5"
                    >
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={item.testId} className="group flex items-center gap-5 rounded-2xl border border-white/8 bg-navy-850/70 p-5">
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}
            <Reveal delay={0.3}>
              <div className="rounded-2xl border border-gold/15 bg-gold/[0.05] p-6">
                <p className="font-display text-lg font-semibold text-slate-50">Prefer to talk right now?</p>
                <p className="mt-1.5 text-sm text-slate-400">Message us on WhatsApp — we typically reply within the hour during business hours.</p>
                <a
                  href={whatsappLink("Hello Faithh! I'd like a free consultation for my business.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-quick"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1faa55] px-6 py-3 text-sm font-semibold text-white transition-[box-shadow,transform,background-color] duration-400 hover:-translate-y-0.5 hover:bg-[#25D366] hover:text-navy-950 hover:shadow-[0_12px_36px_-10px_rgba(37,211,102,0.5)]"
                >
                  <MessageCircle className="h-4 w-4" />
                  Start WhatsApp Chat
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.36}>
              <div className="flex items-center justify-between rounded-2xl border border-white/8 bg-navy-850/70 p-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-slate-500">Follow Faithh</span>
                <div className="flex items-center gap-3">
                  {SOCIALS.map((social) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`contact-social-${social.label.toLowerCase()}`}
                        aria-label={`Faithh on ${social.label}`}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 bg-gold/8 text-gold transition-all duration-400 hover:-translate-y-0.5 hover:border-gold/60 hover:bg-gold/15 hover:shadow-[0_0_24px_rgba(212,175,55,0.3)]"
                      >
                        <Icon className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.12} className="lg:col-span-3">
            <div className="sheen-border relative rounded-3xl border border-gold/20 bg-navy-850/80 p-7 sm:p-10">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="success"
                    data-testid="contact-success"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  >
                    <span className="flex h-20 w-20 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
                      <CheckCircle2 className="h-10 w-10 text-gold" />
                    </span>
                    <h3 className="mt-7 font-display text-2xl sm:text-3xl font-semibold text-slate-50">Thank you!</h3>
                    <p className="mt-3 max-w-md text-sm sm:text-base leading-relaxed text-slate-400">
                      Your enquiry has been received. Our team will get back to you shortly.
                      Want an instant response? Continue the conversation on WhatsApp.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                      <a
                        href={whatsappLink(waMessage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-success-whatsapp"
                        className="inline-flex items-center gap-2 rounded-full bg-[#1faa55] px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,color,transform] duration-400 hover:-translate-y-0.5 hover:bg-[#25D366] hover:text-navy-950"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Continue on WhatsApp
                      </a>
                      <button
                        data-testid="contact-send-another"
                        onClick={() => {
                          setForm(EMPTY);
                          setDone(false);
                        }}
                        className="rounded-full border border-gold/35 px-7 py-3.5 text-sm font-semibold text-gold-soft transition-colors duration-300 hover:bg-gold/10"
                      >
                        Send Another Enquiry
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -16 }}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="full_name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Full Name *</label>
                        <input id="full_name" data-testid="contact-input-name" required minLength={2} maxLength={120} value={form.full_name} onChange={set("full_name")} placeholder="Your name" className={inputClass} />
                      </div>
                      <div>
                        <label htmlFor="business_name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Business / Company</label>
                        <input id="business_name" data-testid="contact-input-business" maxLength={160} value={form.business_name} onChange={set("business_name")} placeholder="Company name" className={inputClass} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Email *</label>
                        <input id="email" type="email" data-testid="contact-input-email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputClass} />
                      </div>
                      <div>
                        <label htmlFor="phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">WhatsApp / Phone *</label>
                        <input id="phone" type="tel" data-testid="contact-input-phone" required minLength={7} maxLength={25} value={form.phone} onChange={set("phone")} placeholder="+971 5X XXX XXXX" className={inputClass} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Service Required *</label>
                      <div className="relative">
                        <select
                          id="service"
                          data-testid="contact-select-service"
                          required
                          value={form.service}
                          onChange={set("service")}
                          className={`${inputClass} appearance-none pr-12 ${form.service ? "text-slate-100" : "text-slate-600"}`}
                        >
                          <option value="" disabled>Select a service</option>
                          {SERVICE_OPTIONS.map((s) => (
                            <option key={s} value={s} className="bg-navy-900 text-slate-100">{s}</option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gold/70" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Tell Us About Your Requirement *</label>
                      <textarea id="message" data-testid="contact-input-message" required minLength={5} maxLength={2000} rows={4} value={form.message} onChange={set("message")} placeholder="A sentence or two about your business and what you need…" className={`${inputClass} resize-none`} />
                    </div>
                    <input
                      type="text"
                      name="website"
                      data-testid="contact-honeypot"
                      value={form.website}
                      onChange={set("website")}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
                    />
                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={sending}
                      className="btn-gold inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm sm:text-base font-semibold text-navy-950 transition-[box-shadow,transform,opacity] duration-400 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-10px_rgba(212,175,55,0.55)] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          Request a Consultation
                          <Send className="h-4 w-4" />
                        </>
                      )}
                    </button>
                    <p className="text-center text-xs text-slate-500">No spam, no pressure — just a straightforward conversation about your business.</p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
